<script setup>
import {onMounted, ref, watch} from "vue";
import DocumentService from "@/services/DocumentService";
import router from "@/router";
import {useDataTable} from "@/utils/datatableHelper";
import BSTooltip from "@/components/tooltips/BSTooltip.vue";
import {useToast} from "vue-toastification";
import {getLoggedUser} from "@/services/AuthService";
import DownloadButtonComponent from '@/components/documents/DownloadButtonComponent.vue';
import {useI18n} from 'vue-i18n'
import {kebabToCamel} from '@/helper';

const {t} = useI18n()

const toast = useToast();

const documents = ref([]);

const isLoading = ref(false);

const modalOpen = ref(false);

/*const stateDescription = ref('');*/

const selectedDocumentId = ref(null);

const props = defineProps({
  filterType: String
});

const loggedUser = ref(null);

const canBeAccepted = ref(props.filterType === 'to-processing');

const canBeReviewed = ref(props.filterType === 'in-solution');

const documentDone = ref(props.filterType === 'archived');

const datatableRef = ref(null)

const datatable = useDataTable(fetchDocuments, 'created_at', 20, true, {}, kebabToCamel('documents-list-' + props.filterType), datatableRef);

const columns = ref([
  {
    label: t('documentsListComponent.name'),
    field: 'name',
    filterOptions: {
      enabled: true,
      skipDiacritics: true,
      placeholder: ' ',
    }
  },
  {
    label: t('documentsListComponent.client'),
    field: 'documentable.fullname',
    filterOptions: {
      enabled: true,
      skipDiacritics: true,
      placeholder: ' '
    }
  },
  {
    label: t('documentsListComponent.assigned_user'),
    field: 'assigned_user.fullname',
    filterOptions: {
      enabled: false,
      skipDiacritics: true,
      placeholder: ' '
    }
  },
  {
    label: t('documentsListComponent.document_state'),
    field: 'document_state',
    filterOptions: {
      enabled: true,
      skipDiacritics: true,
      placeholder: ' '
    },
    // formatFn: translateStates
  },
  {
    label: t('documentsListComponent.created_at'),
    field: 'created_at',
  },
  {
    label: t('documentsListComponent.action'),
    field: 'action',
    sortable: false,
    html: true
  },
]);

onMounted(() => {
  getLoggedUser().then(response => {
    loggedUser.value = response;
  });
});

function fetchDocuments(body) {
  let additionalBody = {
    filter: {
      type: props.filterType
    }
  }
  body.filter = {...body.filter, ...additionalBody.filter};
  DocumentService.getDocuments(body).then(response => {
    datatable.totalRecords.value = response.meta.total;
    datatable.rows.value = response.data;
    datatable.isLoading.value = false;
  });
}

function assignToMyself(document) {
  isLoading.value = true;
  DocumentService.assignToMyself(document.id).then(response => {
    document.assigned_user = loggedUser.value;
  });
}

function unassignMyself(document) {
  isLoading.value = true;
  DocumentService.unassignMyself(document.id).then(response => {
    document.assigned_user = null;
  });
}

function setDocumentState(documentId, state, description = null) {
  hideModal();
  isLoading.value = true;
  let body = {
    state: state,
  }
  if (description) {
    body.description = description;
  }

  DocumentService.setDocumentState(documentId, body).then(response => {
    const index = documents.value.findIndex(doc => doc.id === documentId);
    if (index !== -1) {
      documents.value.splice(index, 1);
      isLoading.value = false;
    }
    toast.success(response.message);
  });
}

function navigateTo(to) {
  router.push(to);
}

function showModal(documentId) {
  selectedDocumentId.value = documentId;
  modalOpen.value = true;
}

function hideModal() {
  modalOpen.value = false;
}

function translateStates(value) {
  if (value === 'PREPARED') {
    return t('documentsListComponent.prepared');
  } else if (value === 'TO_BE_SIGNED') {
    return t('documentsListComponent.to_be_signed');
  } else if (value === 'SIGNED') {
    return t('documentsListComponent.signed');
  } else if (value === 'TO_REVIEW') {
    return t('documentsListComponent.to_review');
  } else if (value === 'ACCEPTED') {
    return t('documentsListComponent.accepted');
  } else if (value === 'CANCELED') {
    return t('documentsListComponent.canceled');
  } else if (value === 'REJECTED') {
    return t('documentsListComponent.rejected');
  } else {
    return value;
  }
}

watch(() => props.filterType, (newFilter, oldFilter) => {
  if (newFilter !== oldFilter) {
    fetchDocuments();
    canBeAccepted.value = props.filterType === 'to-processing';
    canBeReviewed.value = props.filterType === 'in-solution';
    documentDone.value = props.filterType === 'archived';
  }
});

</script>

<template>
  <!--   modals
    <TransitionRoot appear :show="modalOpen" as="template">
      <Dialog as="div" class="modal show d-block" style="z-index: 1055;">
        <div class="modal-backdrop fade show"></div>
        <div class="modal-dialog modal-dialog-centered"  style="z-index: 1055">
          <div class="modal-content">
            <DialogPanel class="modal-body p-4 rounded-lg shadow-lg">
              <div class="modal-header">
                <h3 class="text-lg font-bold">{{ t('documentsListComponent.reject_document') }}</h3>
              </div>
              <div class="modal-body">
                  <div class="mt-3">
                    <div class="row my-3">
                      <div class="col-12">
                        <label for="stateDescription" class="form-label">{{ t('documentsListComponent.description') }}</label>
                        <textarea v-model="stateDescription" class="w-full form-control"/>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-12 text-end">
                        <u class="btn ms-2 text-decoration-underline" @click="hideModal()">{{ t('documentsListComponent.close') }}</u>
                        <Button type="button" @click="setDocumentState(selectedDocumentId, 'CANCELED', stateDescription)" label="Odmítnout"></Button>
                      </div>
                    </div>
                  </div>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>
     end modals -->

  <div class="mt-3">
    <vue-good-table ref="datatableRef"
                    :columns="columns"
                    :rows="datatable.rows.value"
                    mode="remote"
                    @page-change="datatable.onPageChange"
                    @sort-change="datatable.onSortChange"
                    @column-filter="datatable.onColumnFilter"
                    @per-page-change="datatable.onPerPageChange"
                    :totalRows="datatable.totalRecords.value"
                    :pagination-options="{
          enabled: true,
          rowsPerPageLabel: t('documentsListComponent.entriesPerPage'),
          nextLabel: t('documentsListComponent.next'),
          prevLabel: t('documentsListComponent.previous'),
          ofLabel: 'z',
        }"
                    :sort-options="{
          enabled: true,
          initialSortBy: datatable.initialSortBy.value}">
      <template #table-row="props">
        <span v-if="props.column.field == 'assigned_user.fullname'">
          <span>
            <span v-if="props.row.assigned_user">
              {{ props.row.assigned_user.label }}
                <button type="button" class="btn btn-link"><i class="mdi mdi-account-minus" @click="unassignMyself(props.row)"/></button>
            </span>
            <span v-else>
              <button type="button" class="btn btn-secondary btn-sm" @click="assignToMyself(props.row)"><i class="mdi mdi-account-plus"></i> {{ t('documentsListComponent.assign_me') }}</button>
            </span>
          </span>

        </span>
        <span v-if="props.column.field === 'action'">
            <span style="font-weight: bold; float: right">
              <BSTooltip v-if="props.row.state?.state === 'REJECTED'" title="t('documentsListComponent.reject')">
                <button type="button" class="btn btn-link"><i class="mdi mdi-close-circle-outline" @click="setDocumentState(props.row.id, 'CANCELED')"/></button>
              </BSTooltip>
              <BSTooltip v-if="canBeReviewed" :title="t('documentsListComponent.mark_for_processing')">
                <button type="button" class="btn btn-link"><i class="mdi mdi-file-find-outline" @click="setDocumentState(props.row.id, 'TO_REVIEW')"/></button>
              </BSTooltip>
              <BSTooltip v-if="documentDone" :title="t('documentsListComponent.mark_for_processing')">
                <button type="button" class="btn btn-link"><i class="mdi mdi-file-restore-outline" @click="setDocumentState(props.row.id, 'TO_REVIEW')"/></button>
              </BSTooltip>
              <BSTooltip :title="t('documentsListComponent.detail')">
                <router-link :to="{ name: 'DocumentDetail', params: { id: props.row.id} }"><button type="button" class="btn btn-link"><i class="mdi mdi-eye"/></button></router-link>
              </BSTooltip>
              <BSTooltip v-if="props.row.file" :title="t('documentsListComponent.download')">
                <DownloadButtonComponent :uuid="props.row.file?.uuid" :downloadUrl="props.row.file.download_url"/>
              </BSTooltip>
            </span>
        </span>
        <span v-if="props.column.field == 'document_state'">
            <span v-if="props.row.state?.state == 'REJECTED'" class="badge" style="background-color: red;">{{ translateStates(props.row.state?.state) }}</span>
            <span v-else>{{ translateStates(props.row.state?.state) }}</span>
            <span v-if="props.row.client_state" class="d-block">
              Klient: {{ translateStates(props.row.client_state.state) }}
            </span>
        </span>
      </template>
      <template v-slot:emptystate v-if="!datatable.isLoading.value">
        <div class="text-center">
          {{ t('documentsListComponent.no_records_found') }}
        </div>
      </template>
      <template v-slot:emptystate v-if="datatable.isLoading.value">
        <div class="text-center">
          {{ t('documentsListComponent.loading') }}
        </div>
      </template>
    </vue-good-table>
  </div>
</template>

<style scoped>

</style>
